import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../../User/UserContext";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import Logout from "../../pages/Login/logout";
import "../../App.css";
import "./Navbar.css";
import { UserRole } from "../../Utils/enums";

const PI = (name: string, alt: string) => (
  <img src={`/pixel-icons/${name}`} alt={alt} style={{ width: 20, height: 20, imageRendering: "pixelated", objectFit: "contain" }} />
);

interface ProfileProps {
  username: string;
  avatarUrl?: string;
  onNavigate?: () => void;
}

interface NavItemProps {
  label?: string;
  path: string;
  icon?: React.ReactNode;
  iconSrc?: string;
  altText?: string;
  onNavigate?: () => void;
  isCta?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  label,
  path,
  icon,
  iconSrc,
  altText,
  onNavigate,
  isCta = false,
}) => {
  const location = useLocation();
  const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
  
  return (
    <li className="nav-item">
      <Link 
        to={path} 
        className={`nav-button nav-link ${isActive ? "active" : ""} ${isCta ? "cta-button" : ""}`} 
        onClick={onNavigate}
        title={label}
      >
        {icon && <span className="nav-icon-wrapper">{icon}</span>}
        {iconSrc ? (
          <img src={iconSrc} alt={altText || ""} className="nav-icon" />
        ) : (
          <span>{label}</span>
        )}
      </Link>
    </li>
  );
};

export default function Navbar() {
  const userProp = React.useContext(UserContext);
  const user = userProp?.user;
  const userRole = user?.role ?? 0;
  const accountApi = React.useContext(SunsetAPIAccountContext);
  const [avatarUrl, setAvatarUrl] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    let mounted = true;
    const fetchAvatar = async () => {
      if (!user || !accountApi) return;
      try {
        const id = parseInt(user.id);
        const [sessionResp, charsResp] = await Promise.all([
          accountApi.getLastAccountSession(id, user.username),
          accountApi.getCharactersByAccountIDAndNAme(id, user.username),
        ]);

        let skin: number | undefined;
        if (sessionResp && sessionResp.success && sessionResp.data) {
          const charName = sessionResp.data.characterName;
          if (charsResp && charsResp.success && Array.isArray(charsResp.data)) {
            const match = charsResp.data.find((c) => c.characterName === charName);
            skin = match?.lastSkin;
          }
        }

        if (!skin && charsResp && charsResp.success && Array.isArray(charsResp.data) && charsResp.data.length > 0) {
          skin = charsResp.data[0].lastSkin;
        }

        if (mounted) {
          if (skin !== undefined && skin !== null) {
            const idSkin = skin > 300 ? 26 : skin;
            setAvatarUrl(`${window.location.origin}/samp/headshots/${idSkin}.png`);
          } else {
            setAvatarUrl(undefined);
          }
        }
      } catch (err) {
        // silent
      }
    };

    fetchAvatar();
    return () => { mounted = false; };
  }, [user, accountApi]);
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  return (
    <>
      <nav
        className={`navbar-main ${menuOpen ? "nav-open" : ""}`}
        ref={navRef}
        aria-label="Primary navigation"
      >
        <div className="nav-container">
          <Logo />
          <button
            type="button"
            className={`menu-toggle-btn ${menuOpen ? "open" : ""}`}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={toggleMenu}
          >
            <span />
            <span />
            <span />
          </button>
          <ul
            id="primary-navigation"
            className={`nav-list ${menuOpen ? "open" : ""}`}
          >
            <NavItem label="Accounts" path="/accounts" icon={PI("group-of-people-1-Original.png", "Accounts")} onNavigate={closeMenu} />
            <FactionsDropdown onNavigate={closeMenu} />
            <NavItem label="Leaderboard" path="/leaderboard" icon={PI("fakebookcharts-Original.png", "Leaderboard")} onNavigate={closeMenu} />
            <NavItem label="Crates" path="/crates" icon={PI("wooden-box-hinged-Original.png", "Crates")} onNavigate={closeMenu} />
            {userRole >= UserRole.Mod && (
              <Staff role={userRole} onNavigate={closeMenu} />
            )}
            <Community onNavigate={closeMenu} />
            <NavItem label="Donate" path="/donations" icon={PI("wallet-Original.png", "Donate")} isCta={true} onNavigate={closeMenu} />
            {user && (
              <NavItem
                label="Launcher"
                path="/launcher-download"
                icon={PI("personal-jetpack-Original.png", "Launcher")}
                onNavigate={closeMenu}
              />
            )}
          </ul>
          <div className="nav-account">
            {user ? (
              <Profile username={user.username} avatarUrl={avatarUrl} onNavigate={closeMenu} />
            ) : (
              <Link
                to="/login"
                className="nav-button nav-link cta-button"
                onClick={closeMenu}
              >
                {PI("businessman-Original.png", "Login")}
                Login
              </Link>
            )}
          </div>
        </div>
      </nav>
      <div
        className={`nav-overlay ${menuOpen ? "visible" : ""}`}
        onClick={closeMenu}
        aria-hidden
      />
    </>
  );
}

const Profile: React.FC<ProfileProps> = ({ username, avatarUrl, onNavigate }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setIsHovering(false);
  };

  const handleNavigate = () => {
    closeDropdown();
    onNavigate?.();
  };

  const handleTriggerClick = () => {
    if (isHovering) {
      return;
    }
    setIsDropdownOpen((prev) => !prev);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeDropdown();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsDropdownOpen((prev) => !prev);
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };

  return (
    <div
      className={`nav-dropdown profile-menu ${isDropdownOpen ? "open" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="nav-button dropdown-trigger"
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={`${username} avatar`} className="nav-avatar" />
        ) : null}
        <span className="username">{username}</span>
        <span className="chevron" aria-hidden />
      </button>
      <div className="dropdown-content profile-dropdown" role="menu">
        <Link to="/user" role="menuitem" onClick={handleNavigate}>
          <span className="dropdown-icon">{PI("profile-button-Original.png", "Profile")}</span>
          View profile
        </Link>
        <Link to="/logout" role="menuitem" onClick={handleNavigate}>
          <span className="dropdown-icon">{PI("log-out-Original.png", "Logout")}</span>
          <Logout />
        </Link>
      </div>
    </div>
  );
};

interface StaffProps {
  role: number;
  onNavigate: () => void;
}

const Staff: React.FC<StaffProps> = ({ role, onNavigate }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const canAccessManagement = role > UserRole.SnrAdmin;
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setIsHovering(false);
  };
  const handleNavigate = () => {
    closeDropdown();
    onNavigate();
  };

  const handleTriggerClick = () => {
    if (isHovering) {
      return;
    }
    setIsDropdownOpen((prev) => !prev);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeDropdown();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsDropdownOpen((prev) => !prev);
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };

  return (
    <div
      className={`nav-dropdown ${isDropdownOpen ? "open" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="nav-button dropdown-trigger"
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
      >
        Staff
        <span className="chevron" aria-hidden />
      </button>
      <div className="dropdown-content" role="menu">
        <Link to="/logs" role="menuitem" onClick={handleNavigate}>
          <span className="dropdown-icon">{PI("parchment-paper-Original.png", "Logs")}</span>
          Logs
        </Link>
        {canAccessManagement && (
          <>
            <Link to="/management" role="menuitem" onClick={handleNavigate}>
              <span className="dropdown-icon">{PI("gear-icon-Original.png", "Management")}</span>
              Management
            </Link>
            <Link to="/bugs" role="menuitem" onClick={handleNavigate}>
              <span className="dropdown-icon">{PI("green-june-beetle-Original.png", "Bugs")}</span>
              Bugs
            </Link>
            <Link
              to="/management-launcher"
              role="menuitem"
              onClick={handleNavigate}
            >
              <span className="dropdown-icon">{PI("personal-jetpack-Original.png", "Manage Launcher")}</span>
              Manage Launcher
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

interface CommunityProps {
  onNavigate: () => void;
}

const Community: React.FC<CommunityProps> = ({ onNavigate }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setIsHovering(false);
  };
  const handleNavigate = () => {
    closeDropdown();
    onNavigate();
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeDropdown();
  };

  const handleTriggerClick = () => {
    if (isHovering) {
      return;
    }
    setIsDropdownOpen((prev) => !prev);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsDropdownOpen((prev) => !prev);
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };

  return (
    <div
      className={`nav-dropdown ${isDropdownOpen ? "open" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="nav-button dropdown-trigger"
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
      >
        Community
        <span className="chevron" aria-hidden />
      </button>
      <div className="dropdown-content" role="menu">
        <Link to="/help" role="menuitem" onClick={handleNavigate}>
          <span className="dropdown-icon">{PI("question-mark-Original.png", "Help")}</span>
          Help
        </Link>
        <a
          className="my-link"
          href="https://forum.ss-rp.com/"
          target="_blank"
          rel="noreferrer noopener"
          onClick={handleNavigate}
        >
          <span className="dropdown-icon">{PI("message-board-Original.png", "Forums")}</span>
          Forums
        </a>
        <a
          className="my-link"
          href="https://discord.gg/2Y3t5B9XCe"
          target="_blank"
          rel="noreferrer noopener"
          onClick={handleNavigate}
        >
          <span className="dropdown-icon">{PI("link-icon-Original.png", "Discord")}</span>
          Discord
        </a>
      </div>
    </div>
  );
};

const Logo: React.FC = () => (
  <Link to="/" className="logo-link" aria-label="Return to dashboard">
    <img
      src={`${window.location.origin}/logo.png`}
      alt="Server logo"
      className="logo"
    />
  </Link>
);

interface FactionsDropdownProps {
  onNavigate: () => void;
}

const FactionsDropdown: React.FC<FactionsDropdownProps> = ({ onNavigate }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setIsHovering(false);
  };
  const handleNavigate = () => {
    closeDropdown();
    onNavigate();
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeDropdown();
  };

  const handleTriggerClick = () => {
    if (isHovering) return;
    setIsDropdownOpen((prev) => !prev);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsDropdownOpen((prev) => !prev);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };

  return (
    <div
      className={`nav-dropdown ${isDropdownOpen ? "open" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="nav-button dropdown-trigger"
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
      >
        {PI("blue-shield-Original.png", "Factions")}
        Factions
        <span className="chevron" aria-hidden />
      </button>
      <div className="dropdown-content" role="menu">
        <Link to="/factions" role="menuitem" onClick={handleNavigate}>
          <span className="dropdown-icon">{PI("blue-shield-Original.png", "Factions")}</span>
          Factions
        </Link>
        <Link to="/wars" role="menuitem" onClick={handleNavigate}>
          <span className="dropdown-icon">{PI("blue-shield-Original.png", "Wars")}</span>
          Wars
        </Link>
      </div>
    </div>
  );
};
